import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type AnalyticValues, useAnalytics } from "../../../../contexts/analytics/hooks";
import type { OpenShiftRequest, ReplaceShiftRequest } from "../../../../utils/models";
import { usePostToAgencyControl } from "../../../RequestData/hooks/usePostToAgencyControl";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { agencyRowCopy } from "./gapsPostedSteps";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** The agency an open shift was posted to (its `agency_source_name`); `undefined` for another kind, or none yet. */
	agencyName: string | undefined;
	/** The posted sheet's own event properties (department, request, type). */
	properties: AnalyticValues;
	testIdPrefix: string;
};

/**
 * "Send to agency" on a posted shift (P3-10): `usePostToAgencyControl` / `useCanPostRequestToAgency`, with the control's
 * own confirm dialog, which is always mounted — the row itself only where the request can reach an agency at all.
 */
export const GapsPostedAgencyRow: FC<Props> = ({ request, agencyName, properties, testIdPrefix }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const agency = usePostToAgencyControl(request);
	const agencyRow = agencyRowCopy(request, {
		// Key kept as-is on purpose: renaming it would break the existing dashboards. The SOURCE is
		// now availability (configured AND active), which is what the count was always meant to mean.
		configuredAgencyCount: agency.availableAgencies.length,
		isPostedToAgency: agency.isPostedToAgency,
		canNewlyPostToAgency: agency.canNewlyPostToAgency,
	});
	return (
		<>
			{agencyRow !== null && (
				<SheetActionRow
					icon={<ApartmentOutlinedIcon />}
					label={agencyRow.isSent ? t("gaps.postedSheet.agencySent", { agency: agencyName }) : t("gaps.postedSheet.agency")}
					description={t(agencyRow.descriptionKey)}
					showChevron={agencyRow.isActionable}
					disabled={!agencyRow.isActionable}
					loading={agency.isPosting}
					onClick={() => {
						track("gaps_posted_agency_opened", properties);
						agency.openDialog();
					}}
					dataTestId={`${testIdPrefix}-agency`}
				/>
			)}
			{agency.dialog}
		</>
	);
};
